/**
 * Agreement Gate — Existing investors must sign the updated agreement
 *
 * Route: /agreement
 *
 * Two-step flow:
 * 1. Info screen explaining the 1% platform fee
 * 2. Updated terms + type-to-sign
 */

import { useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { Dialog } from '@/common/components/dialog'
import { useAuth } from '@/common/hooks/useAuth'
import { useUserProfile } from '@/common/hooks/useUserProfile'
import { getDefaultRouteForRole } from '@/common/utils/getDefaultRouteForRole'
import { dynamicPath } from '@/common/utils/navigation'
import { TypeToSign } from '@/features/agreement/components/TypeToSign'
import { TermsContent } from '@/features/onboarding/content/termsContent'

import * as styles from './agreement.css'

export const Route = createFileRoute({
  component: AgreementPage,
})

type AgreementStep = 'info' | 'sign'

function AgreementPage() {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { redirect?: string }
  const { roles, logout } = useAuth()
  const { data, signAgreement, needsNewAgreement } = useUserProfile()
  const [step, setStep] = useState<AgreementStep>('info')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [signError, setSignError] = useState<string | null>(null)

  const redirectTo = search.redirect || getDefaultRouteForRole(roles)

  // If the user has already signed, redirect them
  if (data && !needsNewAgreement) {
    navigate({ to: dynamicPath(redirectTo), replace: true })
    return null
  }

  const investorName = data?.profile
    ? `${data.profile.first_name} ${data.profile.last_name}`.trim() || 'Your Name'
    : 'Your Name'

  const handleSign = async (fullLegalName: string, nameMismatchConfirmed = false) => {
    setIsSubmitting(true)
    setSignError(null)
    try {
      await signAgreement(fullLegalName, nameMismatchConfirmed)
      navigate({ to: dynamicPath(redirectTo), replace: true })
    } catch (err) {
      console.error('Failed to sign agreement:', err)
      setSignError('Failed to sign agreement. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      {/* Step 1: Modal overlay explaining the platform fee */}
      {step === 'info' && (
        <Dialog open onOpenChange={() => {}}>
          <Dialog.Content
            width={640}
            aria-label="Updated Investor Agreement"
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <div className={styles.header}>
              <img
                src="/images/orchard-offers.svg"
                alt="Orchard Cash Offer"
                className={styles.logo}
              />
              <Text as="h1" variant="h4" weight="bold" className={styles.title}>
                Updated Investor Agreement
              </Text>
              <Text variant="body" className={styles.subtitle}>
                We have an important update to share with you.
              </Text>
            </div>

            <div className={styles.infoBody}>
              <p>
                Since launching Orchard Offers, we've been grateful for the strong response from investors
                like you. The marketplace has grown quickly, and we're focused on making it the best place
                to access high-quality off-market opportunities — which means continuing to invest in the
                platform, the agent network behind it, and the tools that help you close faster.
              </p>
              <p>
                To support that growth, we're introducing a{' '}
                <strong>1% platform fee on all deals closed through Orchard Offers</strong>, effective
                April 6, 2026.
              </p>

              <p><strong>How it works:</strong></p>
              <ul>
                <li>A 1% fee applies to transactions initiated through the marketplace</li>
                <li>The fee is only due at closing — no upfront costs</li>
                <li>Reduce your fee to 0.5% when you close with Orchard Title</li>
              </ul>

              <p><strong>What this enables:</strong></p>
              <ul>
                <li>Continued free access to off-market inventory — no subscriptions or listing fees</li>
                <li>More deal flow through continued investment in growing our agent network</li>
                <li>Faster, more seamless transactions through ongoing product improvements</li>
              </ul>

              <p>
                To continue using Orchard Offers, please review and sign the updated investor agreement
                on the next screen.
              </p>
            </div>

            <Button
              variant="primary"
              onClick={() => setStep('sign')}
              className={styles.continueButton}
            >
              Continue
            </Button>
          </Dialog.Content>
        </Dialog>
      )}

      {/* Step 2: Full-page terms + type-to-sign */}
      {step === 'sign' && (
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.header}>
              <img
                src="/images/orchard-offers.svg"
                alt="Orchard Cash Offer"
                className={styles.logo}
              />
              <Text as="h1" variant="h4" weight="bold" className={styles.title}>
                Review & Accept Updated Terms
              </Text>
              <Text variant="body" className={styles.subtitle}>
                Please review the updated agreement below and sign to continue.
              </Text>
            </div>

            <div className={styles.termsScrollArea}>
              <TermsContent />
            </div>
            <TypeToSign
              investorName={investorName}
              onSign={handleSign}
              isSubmitting={isSubmitting}
            />
            {signError && (
              <Text variant="bodySm" style={{ color: '#d32f2f', textAlign: 'center', marginTop: 12 }}>
                {signError}
              </Text>
            )}
            <Text variant="bodySm" style={{ textAlign: 'center', marginTop: 16, color: '#6F7082' }}>
              Need to use a different account?{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); logout() }} style={{ color: '#6F7082', textDecoration: 'underline' }}>
                Log out
              </a>
            </Text>
          </div>
        </div>
      )}
    </>
  )
}
